import { useParams, useNavigate } from 'react-router-dom'
import { useViewMode } from '@/stores/viewMode'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Edit, Settings, Clock, FileVideo } from 'lucide-react'
import { useAdScheduleTemplateById } from '@/hooks/useAdScheduleTemplateById'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import { Separator } from '@/components/ui/separator'
import { ScrollArea } from '@/components/ui/scroll-area'

export default function ViewTemplatePage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { mode, id: viewModeId } = useViewMode()
  const scopePrefix = mode === 'provider' && viewModeId ? `/provider/${viewModeId}` : '/admin'
  const { template, isLoading } = useAdScheduleTemplateById({
    template_id: id || null,
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">找不到模版</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          返回列表
        </Button>
      </div>
    )
  }

  const activeSlots = template.slots.filter((slot) => slot.is_active)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{template.template_name}</h1>
            <p className="text-muted-foreground">
              建立於{' '}
              {format(new Date(template.created_at), 'yyyy年MM月dd日 HH:mm', { locale: zhTW })}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`${scopePrefix}/ad-schedule-templates/${id}/apply`)}
          >
            <Settings className="mr-2 h-4 w-4" />
            套用設定
          </Button>
          <Button onClick={() => navigate(`${scopePrefix}/ad-schedule-templates/${id}/edit`)}>
            <Edit className="mr-2 h-4 w-4" />
            編輯模版
          </Button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-muted-foreground">模版名稱</p>
              <p className="font-medium">{template.template_name}</p>
            </div>
            {template.description && (
              <div>
                <p className="text-sm text-muted-foreground">描述</p>
                <p className="font-medium">{template.description}</p>
              </div>
            )}
            <div>
              <p className="text-sm text-muted-foreground">狀態</p>
              {template.is_active ? (
                <Badge className="bg-green-500">啟用</Badge>
              ) : (
                <Badge variant="secondary">停用</Badge>
              )}
            </div>
            <div>
              <p className="text-sm text-muted-foreground">更新時間</p>
              <p className="font-medium">
                {format(new Date(template.updated_at), 'yyyy/MM/dd HH:mm', { locale: zhTW })}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>梯次概覽</CardTitle>
            <CardDescription>共有 {activeSlots.length} 個啟用的梯次</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2">
              {activeSlots.map((slot) => {
                const totalAds = slot.slot_items.filter((item) => item.is_active).length
                return (
                  <div key={slot.slot_id} className="p-4 rounded-lg border bg-muted/20">
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{slot.slot_name || `梯次 ${slot.slot_number}`}</p>
                      <Badge variant="outline">梯次 {slot.slot_number}</Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <FileVideo className="h-4 w-4" />
                        {totalAds} 個廣告
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {slot.total_duration} 秒
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>梯次詳細內容</CardTitle>
          <CardDescription>查看每個梯次的廣告排程</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[600px]">
            <div className="space-y-6">
              {activeSlots.map((slot, index) => (
                <div key={slot.slot_id}>
                  {index > 0 && <Separator className="my-6" />}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">
                        {slot.slot_name || `梯次 ${slot.slot_number}`}
                      </h3>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary">
                          <Clock className="mr-1 h-3 w-3" />
                          總時長: {slot.total_duration} 秒
                        </Badge>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {slot.slot_items
                        .filter((item) => item.is_active)
                        .sort((a, b) => a.order - b.order)
                        .map((item, itemIndex) => (
                          <div
                            key={item.item_id}
                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                          >
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-background text-sm font-medium">
                              {itemIndex + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">
                                {item.advertisement.title || '無標題廣告'}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {item.advertisement.media.length} 個媒體檔案
                              </p>
                            </div>
                            <Badge variant="outline">{item.play_duration} 秒</Badge>
                          </div>
                        ))}
                      {slot.slot_items.filter((item) => item.is_active).length === 0 && (
                        <p className="text-center text-muted-foreground py-4">此梯次尚未加入廣告</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
